import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Platform, Pressable, StyleSheet, View, Animated } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import useResponsiveness from '@/hooks/useResponsiveness';

const createStyles = (colors) => StyleSheet.create({
  parameterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: 'transparent',
    borderRadius: 17,
    height: 34,
    paddingHorizontal: 8,
    minWidth: 34,
    ...Platform.select({
      web: {
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      }
    })
  },
  parameterButtonDot: {
    position: 'absolute',
    top: 3,
    right: 3,
    width: 10,
    height: 10,
    borderRadius: 10,
    backgroundColor: 'rgb(255, 40, 40)',
    borderWidth: 2,
    borderColor: colors.solid_color_primary,
  },
  parameterButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  parameterButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  parameterButtonInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  toggleSelectedIndicator: {
    marginLeft: -12,
    height: 12,
    width: 12,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.solid_color_secondary,
    padding: 2,
    borderRadius: 10,
  },
});

const ParameterButton = ({
  icon,
  text,
  iconName, // For custom icon name (e.g., dynamic based on hover)
  iconColor,
  textColor = 'primary',
  onPress,
  onHoverIn,
  onHoverOut,
  isHovered: externalHovered,
  disabled = false,
  showValidationDot = false,
  isHighlighted = false, // Show glowing animation for missing required parameters
  // Custom content renderer (for special cases like toggle indicator)
  renderCustomContent,
  // Additional style overrides
  additionalStyles = [],
  // For indicator type - special handling
  isIndicator = false,
  // For selected action - shows xmark
  showXmark = false,
  // For toggle - shows indicator instead of text
  isToggle = false,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { isXS } = useResponsiveness();
  const [internalHovered, setInternalHovered] = useState(false);
  
  // Glow animation for highlighted state
  const glowAnim = useRef(new Animated.Value(0)).current;
  
  useEffect(() => {
    if (isHighlighted) {
      // Pulsing glow animation
      Animated.loop(
        Animated.sequence([
          Animated.timing(glowAnim, {
            toValue: 1,
            duration: 500,
            useNativeDriver: false,
          }),
          Animated.timing(glowAnim, {
            toValue: 0,
            duration: 500,
            useNativeDriver: false,
          }),
        ])
      ).start();
    } else {
      glowAnim.stopAnimation();
      glowAnim.setValue(0);
    }
    
    return () => {
      glowAnim.stopAnimation();
    };
  }, [isHighlighted, glowAnim]);
  
  // Use external hover state if provided, otherwise use internal
  const isHovered = externalHovered !== undefined ? externalHovered : internalHovered;
  
  const handleHoverIn = () => {
    if (onHoverIn) {
      onHoverIn();
    } else {
      setInternalHovered(true);
    }
  };
  
  const handleHoverOut = () => {
    if (onHoverOut) {
      onHoverOut();
    } else {
      setInternalHovered(false);
    }
  };

  // Determine icon name (for indicator type with hover state)
  const getIconName = () => {
    if (iconName) return iconName;
    if (isIndicator && isHovered && !isXS) return 'xmark';
    return icon;
  };

  // Determine icon color
  const getIconColor = () => {
    if (disabled) return colors.text_color_tertiary;
    // Indicators sit on the composer surface, not on an accent fill — use readable accent text.
    if (isIndicator) return colors.accent_text_color;
    if (iconColor) return iconColor;
    return colors.text_color_primary;
  };

  // Determine text color
  const getTextColor = () => {
    if (isIndicator) return 'accent';
    if (textColor === 'accent_bg') return 'accent_bg';
    if (disabled || (textColor === 'tertiary')) return 'tertiary';
    return 'primary';
  };

  // Animated styles for glow effect
  const animatedGlowStyle = isHighlighted ? {
    // Native shadow props for iOS/Android
    ...(Platform.OS !== 'web' ? {
      shadowColor: 'rgb(255, 100, 100)',
      shadowOpacity: glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0.3, 0.8],
      }),
      shadowRadius: glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [4, 12],
      }),
      shadowOffset: { width: 0, height: 0 },
    } : {}),
    // CSS boxShadow for web
    ...(Platform.OS === 'web' ? {
      boxShadow: glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ['0 0 4px rgba(255, 100, 100, 0.4)', '0 0 12px rgba(255, 100, 100, 0.8)'],
      }),
    } : {}),
  } : {};

  const ButtonContent = (
    <>
      <View style={styles.parameterButtonInner}>
        {/* Icon */}
        {getIconName() && (
          <Icon
            name={getIconName()}
            size={14}
            color={getIconColor()}
          />
        )}

        {/* Custom content renderer (for special cases) */}
        {renderCustomContent && renderCustomContent()}

        {/* Text or Toggle Indicator */}
        {text && !renderCustomContent && (
          <>
            {isToggle && typeof text !== 'string' ? (
              <View style={styles.toggleSelectedIndicator}>
                <Icon name='check' size={8} color={colors.text_color_primary} />
              </View>
            ) : (
              <Text
                size='s'
                weight='n'
                numberOfLines={1}
                ellipsizeMode="tail"
                color={getTextColor()}
              >
                {text}
              </Text>
            )}
          </>
        )}

        {/* Xmark for selected action */}
        {showXmark && (
          <Icon
            name="xmark"
            size={12}
            color={colors.text_color_primary}
          />
        )}

        {/* Xmark for indicator on mobile */}
        {isIndicator && isXS && (
          <Icon
            name="xmark"
            size={12}
            color={colors.accent_text_color}
          />
        )}
      </View>

      {/* Validation dot */}
      {showValidationDot && <View style={styles.parameterButtonDot} />}
    </>
  );

  // Use Animated.View wrapper when highlighted for glow effect
  if (isHighlighted) {
    return (
      <Animated.View style={[{ borderRadius: 17 }, animatedGlowStyle]}>
        <Pressable
          style={[
            styles.parameterButton,
            isHovered && styles.parameterButtonHovered,
            disabled && styles.parameterButtonDisabled,
            { backgroundColor: 'rgba(255, 100, 100, 0.15)' },
            ...additionalStyles
          ]}
          onPress={onPress}
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          disabled={disabled}
        >
          {ButtonContent}
        </Pressable>
      </Animated.View>
    );
  }

  return (
    <Pressable
      style={[
        styles.parameterButton,
        isHovered && styles.parameterButtonHovered,
        disabled && styles.parameterButtonDisabled,
        ...additionalStyles
      ]}
      onPress={onPress}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      disabled={disabled}
    >
      {ButtonContent}
    </Pressable>
  );
};

export default ParameterButton;

